import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../shared/components/Icons';
import ReviewPage from '../../shared/booking/ReviewPage';
import { ExpiredSelection } from '../../shared/booking/FlowParts';
import { checkoutTransfer } from '../../api/transfers';
import { useAuth } from '../../auth/AuthContext';
import TransferSummary from './TransferSummary';
import { clock, journeyFromType, journeyLabel, passengersLabel, shortPlace, splitDateTime } from './searchModel';
import { freeCancellation } from './data';

const checkoutStatus = { Confirmed: 'confirmed', Pending: 'pending' };

/** Booking summary saved with the booking and shown by TransferSummary. */
export function transferSummary(review, { flightNo, request } = {}) {
  const { journey, offer } = review;
  const pickup = splitDateTime(journey.pickupDateTime);
  const back = journey.returnDateTime ? splitDateTime(journey.returnDateTime) : null;
  return {
    label: offer.vehicle.label,
    similarModels: offer.vehicle.similarModels,
    imageUrl: offer.vehicle.imageUrl,
    seats: offer.vehicle.passengerCapacity,
    bags: offer.vehicle.luggageCapacity,
    journey: journeyLabel(journeyFromType(journey.journeyType)),
    roundTrip: journey.tripType === 'RoundTrip',
    pickup: journey.origin.displayAddress,
    drop: journey.destination.displayAddress,
    date: pickup.date,
    time: pickup.time,
    returnDate: back?.date || '',
    returnTime: back?.time || '',
    passengers: journey.passengers,
    distance: journey.distance,
    duration: journey.durationMinutes,
    waitingTime: offer.policies.waitingTime,
    meetAndGreet: journey.journeyType === 'AirportTransfer' ? offer.policies.meetAndGreet : null,
    freeCancellation: freeCancellation(offer.policies.cancellation),
    cancellation: offer.policies.cancellation,
    flightNo: flightNo || '',
    request: request || '',
    supplierCode: offer.supplierCode,
  };
}

export default function TransferReview() {
  const { state } = useLocation();
  const { auth, signInWith } = useAuth();
  const [flightNo, setFlightNo] = useState('');
  const [request, setRequest] = useState('');
  if (!state?.review) return <ExpiredSelection module="transfers" />;

  const { review, backTo } = state;
  const { journey, offer } = review;
  const airport = journey.journeyType === 'AirportTransfer';
  const extras = { flightNo: flightNo.replace(/\s+/g, '').toUpperCase(), request: request.trim() };
  const summary = transferSummary(review, extras);

  const submit = async ({ travellers: [lead], contact, fare, payment, promoCode, paymentId }) => {
    const result = await checkoutTransfer({
      reviewId: review.reviewId,
      contact: { email: contact.email.trim(), phone: contact.phone.trim() },
      passenger: { title: lead.title || null, firstName: lead.firstName.trim(), lastName: lead.lastName.trim() },
      flightNumber: extras.flightNo || null,
      specialRequest: extras.request || null,
      promoCode,
      // The API stops before booking if its fee or discount no longer gives this total.
      expectedTotal: fare.total,
      paymentMethod: payment,
      paymentId,
    }, auth?.accessToken);

    if (result.status === 'Failed') {
      throw new Error("The cab operator couldn't confirm this booking, so you haven't been charged. Please choose another cab.");
    }
    if (result.auth) signInWith(result.auth);
    return {
      status: checkoutStatus[result.status] || 'pending',
      ref: result.supplierBookingId,
      supplierCode: offer.supplierCode,
      supplierBookingId: result.supplierBookingId,
      trackingLink: result.trackingLink || null,
      account: { status: result.accountStatus, email: result.accountEmail, emailSent: result.emailSent },
    };
  };

  const contactNote = (
    <p className="muted small form-note account-note">
      Your driver will call this number, so use a mobile you'll have with you.
      {!auth?.accessToken && (
        <> Have a Trawtel account? <Link className="link small" to={`/account/login?returnTo=${encodeURIComponent(backTo)}`}>Sign in</Link> first,
          or we'll add this booking to the account for this email — and create one if it doesn't exist yet.</>
      )}
    </p>
  );

  const rideDetails = (
    <section className="panel">
      <h3><Icon name="transfers" size={18} /> Ride details</h3>
      <div className="form-grid">
        {airport && (
          <label className="field">
            <span className="field-label">Flight number (recommended)</span>
            <input value={flightNo} onChange={(e) => setFlightNo(e.target.value)} placeholder="e.g. 6E 2134"
              pattern="[A-Za-z0-9]{2}[ \-]?[0-9]{1,5}" title="Airline code and number, e.g. 6E 2134" />
            <span className="field-hint">The driver tracks your flight if it lands early or late.</span>
          </label>
        )}
        <label className="field">
          <span className="field-label">Note for the driver (optional)</span>
          <input value={request} onChange={(e) => setRequest(e.target.value)} maxLength={250} placeholder="e.g. Child seat, extra luggage" />
          <span className="field-hint">Requests depend on availability and may cost extra.</span>
        </label>
      </div>
    </section>
  );

  return (
    <ReviewPage
      module="transfers"
      heading="Review your cab"
      back={{ to: backTo, label: 'Change cab' }}
      summary={<TransferSummary summary={summary} />}
      info={[
        offer.policies.driverAssignment || 'Driver name and number are shared before pickup.',
        airport
          ? (offer.policies.meetAndGreet ? `Meet & greet: ${offer.policies.meetAndGreet}.` : 'Your driver will meet you at arrivals.')
          : 'Please be ready at the pickup point a few minutes before the pickup time.',
        summary.freeCancellation ? `${summary.freeCancellation}.` : 'This booking cannot be cancelled for free.',
      ]}
      travellers={[{ type: 'Adult', label: 'Lead passenger', note: 'Name the driver will ask for', fields: ['title', 'firstName', 'lastName'] }]}
      afterTravellers={rideDetails}
      travelDate={summary.date}
      travelEnd={summary.returnDate || summary.date}
      fareLines={[[`Base fare${summary.roundTrip ? ' · both ways' : ''}`, offer.price.baseFare], ['Taxes', offer.price.taxes, 'tax']]}
      showGst={false}
      onSubmit={submit}
      submitNotice="The cab is booked with the operator on confirmation."
      initialContact={auth?.user?.email ? { email: auth.user.email } : undefined}
      contactNote={contactNote}
      buildBooking={() => ({
        title: `${summary.label} · ${shortPlace(summary.pickup)} → ${shortPlace(summary.drop)}`,
        subtitle: `${clock(summary.time)} pickup · ${passengersLabel(summary.passengers)}${summary.roundTrip ? ' · Round trip' : ''}`,
        travelDate: summary.date,
        endDate: summary.returnDate || summary.date,
        summary,
      })}
    />
  );
}
